import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type TaskList, TaskListForm } from './TaskListForm';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/task-lists/search/edit/$id')(
    {
        staticData: {
            metadata: {
                title: 'Edit task list',
            },
            ui: {
                title: 'Edit task list',
            },
            presentation: 'overlay',
            modules: ['claim'],
        },
        params: {
            parse: ({ id }) => ({ id: BigInt(id) }),
        },
        component: EditTaskList,
    },
);

function EditTaskList() {
    const { id: taskListId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const [open, setOpen] = useState(true);
    const { data: taskList, isPending: isLoading } = trpc.taskList.getTaskList.useQuery(
        { id: taskListId },
        { refetchOnWindowFocus: false },
    );
    const { mutate: updateTaskList, isPending: isUpdating } = trpc.taskList.updateTaskList.useMutation();

    const handleSubmit = (values: TaskList) => {
        if (!taskListId) return;
        updateTaskList(
            { id: taskListId, set: { name: values.name ?? '' } },
            {
                onSuccess: () => {
                    message.success('Task list updated');
                    trpcUtils.taskList.listTaskLists.invalidate();
                    setOpen(false);
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    };
    return (
        <PageController route={Route}>
            <TaskListForm
                open={open}
                mode="edit"
                data={taskList}
                state={match({ isLoading, isUpdating })
                    .with({ isLoading: true }, () => 'loading' as const)
                    .with({ isUpdating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/claims-products/claims/task-lists/search',
                        params: { orgId },
                    });
                }}
            />
        </PageController>
    );
}
